// ONBOARDING V1 — approved copy and production plates.
// Copy is the Founder-approved English wording, verbatim. Spanish onboarding copy has not been
// supplied; it must arrive as a reviewed package before any localized string is published here.

export const ONBOARDING_TOTAL_STEPS = 4;

export const PLATE_BASE = '/onboarding/v1';

export const PLATES = {
  1: '01-welcome',
  2: '02-name',
  3: '03-what',
  4: '04-meet',
};

// Localized legibility treatments, tuned per plate. No full-frame darkening.
export const SCRIMS = {
  1: {
    mobile: 'linear-gradient(to bottom, rgba(5,7,15,0.72) 0%, rgba(5,7,15,0.42) 18%, rgba(5,7,15,0.62) 42%, rgba(5,7,15,0.44) 62%, rgba(5,7,15,0.80) 100%)',
    desktop: 'linear-gradient(100deg, rgba(5,7,15,0.90) 0%, rgba(5,7,15,0.74) 32%, rgba(5,7,15,0.30) 58%, rgba(5,7,15,0.12) 100%)',
  },
  2: {
    mobile: 'linear-gradient(to bottom, rgba(5,7,15,0.62) 0%, rgba(5,7,15,0.30) 22%, rgba(5,7,15,0.52) 52%, rgba(5,7,15,0.82) 78%, rgba(5,7,15,0.88) 100%)',
    desktop: 'linear-gradient(100deg, rgba(5,7,15,0.92) 0%, rgba(5,7,15,0.78) 38%, rgba(5,7,15,0.34) 66%, rgba(5,7,15,0.10) 100%)',
  },
  3: {
    mobile: 'linear-gradient(to bottom, rgba(5,7,15,0.80) 0%, rgba(5,7,15,0.62) 20%, rgba(5,7,15,0.74) 44%, rgba(5,7,15,0.86) 70%, rgba(5,7,15,0.92) 100%)',
    desktop: 'linear-gradient(100deg, rgba(5,7,15,0.94) 0%, rgba(5,7,15,0.84) 40%, rgba(5,7,15,0.44) 70%, rgba(5,7,15,0.18) 100%)',
  },
  4: {
    mobile: 'linear-gradient(to bottom, rgba(5,7,15,0.58) 0%, rgba(5,7,15,0.28) 24%, rgba(5,7,15,0.50) 54%, rgba(5,7,15,0.82) 80%, rgba(5,7,15,0.90) 100%)',
    desktop: 'linear-gradient(100deg, rgba(5,7,15,0.92) 0%, rgba(5,7,15,0.76) 36%, rgba(5,7,15,0.30) 64%, rgba(5,7,15,0.08) 100%)',
  },
};

const EN = {
  wordmark: 'Arvexis',
  back: 'Back',
  continue: 'Continue',

  welcome: {
    eyebrow: 'A CALMER PLACE TO BE YOU',
    headline: 'Welcome to Arvexis.',
    body: ['You don’t need to have anything figured out.', 'We’ll begin with something simple.'],
  },

  name: {
    eyebrow: 'GETTING TO KNOW YOU',
    headline: 'What should I call you?',
    support: 'Whatever feels natural.',
    placeholder: 'Name',
    atmospheric: 'YOUR THOUGHTS. YOUR QUESTIONS. YOUR WORLD.',
    saveError: 'That didn’t save. Please try again.',
  },

  agency: {
    eyebrow: 'YOUR THOUGHTS. YOUR QUESTIONS. YOUR WORLD.',
    headline: 'A private AI companion that gets to know you over time.',
    body: 'Talk naturally, share what’s on your mind, and bring your questions, ideas, photos, and everyday thoughts.',
    rows: [
      { title: 'Talk freely', detail: 'Real conversation, not a rigid script.' },
      { title: 'Context can carry forward', detail: 'Arvexis can remember useful things over time.' },
      {
        title: 'You have choices',
        detail: 'You can save or let Talk transcripts disappear, delete conversations, and ask Arvexis to forget something she remembers.',
      },
    ],
  },

  meet: {
    readyNamed: (firstName) => `You’re ready, ${firstName}.`,
    ready: 'You’re ready.',
    headline: 'Meet Arvexis.',
    body: ['There’s no right way to begin.', 'Ask anything, share anything, or simply say hello.'],
    cta: 'Start a conversation',
    completeError: 'That didn’t finish saving. Please try again.',
    skip: 'Go to Arvexis anyway',
  },
};

// Approved Spanish package. Neutral phrasing; Arvexis kept as a proper name.
const ES = {
  tightEyebrow: true,
  wordmark: 'Arvexis',
  back: 'Atrás',
  continue: 'Continuar',

  welcome: {
    eyebrow: 'UN LUGAR MÁS TRANQUILO PARA SER TÚ',
    headline: 'Te damos la bienvenida a Arvexis.',
    body: ['No tienes que tenerlo todo resuelto.', 'Empecemos con algo sencillo.'],
  },

  name: {
    eyebrow: 'EMPEZANDO A CONOCERTE',
    headline: '¿Cómo quieres que te llame?',
    support: 'Lo que te resulte más natural.',
    placeholder: 'Nombre',
    atmospheric: 'TUS PENSAMIENTOS. TUS PREGUNTAS. TU MUNDO.',
    saveError: 'No se guardó. Inténtalo de nuevo.',
  },

  agency: {
    eyebrow: 'TUS PENSAMIENTOS. TUS PREGUNTAS. TU MUNDO.',
    headline: 'Una compañera de IA privada que llega a conocerte con el tiempo.',
    body: 'Habla con naturalidad, comparte lo que tienes en mente y trae tus preguntas, ideas, fotos y pensamientos del día a día.',
    rows: [
      { title: 'Habla con libertad', detail: 'Una conversación real, no un guion rígido.' },
      { title: 'El contexto puede continuar contigo', detail: 'Arvexis puede recordar cosas útiles con el tiempo.' },
      {
        title: 'Tú tienes opciones',
        detail: 'Puedes guardar una transcripción de Talk o dejar que desaparezca, eliminar conversaciones y pedirle a Arvexis que olvide algo que le hayas contado.',
      },
    ],
  },

  meet: {
    readyNamed: (firstName) => `Todo está listo, ${firstName}.`,
    ready: 'Todo está listo.',
    headline: 'Conoce a Arvexis.',
    body: ['No hay una forma correcta de empezar.', 'Pregunta lo que quieras, comparte lo que quieras o simplemente di hola.'],
    cta: 'Empieza una conversación',
    completeError: 'No terminó de guardarse. Inténtalo de nuevo.',
    skip: 'Continuar a Arvexis de todos modos',
  },
};

export const getOnboardingCopy = (lang) => (lang === 'es' ? ES : EN);
